import { type FC, useCallback, useEffect, useRef, useState } from "react"

import type { LandingPageFaqBlock } from "@forento/core/models/landing"
import { landingPageCopyCharacterLimits } from "@forento/core/schemas/landing"

import Button from "~/components/Button"

import EditableField from "./EditableField"
import EditorShell from "./EditorShell"
import RichEditableField from "./RichEditableField"

type Props = { block: LandingPageFaqBlock; onChange(block: LandingPageFaqBlock): void; onClose(): void }
const FaqBlockEditor: FC<Props> = ({ block, onChange, onClose }) => {
	const nextItemId = useRef(block.content.items.length)

	const [title, setTitle] = useState(block.content.title ?? "")
	const [items, setItems] = useState(block.content.items)
	const [itemIds, setItemIds] = useState(() => block.content.items.map((_, index) => index))

	useEffect(() => {
		const savedItems = items
			.map(item => ({
				question: item.question.trim(),
				answer: item.answer,
			}))
			.filter(item => item.question.length > 0)

		onChange({
			type: "faq",
			content: {
				title: title.trim() || undefined,
				items: savedItems.length > 0 ? savedItems : [{ question: "Question", answer: "" }],
			},
		})
	}, [items, onChange, title])

	const handleQuestionChange = (index: number, value: string) => {
		setItems(prev => {
			const newItems = [...prev]
			newItems[index] = { ...newItems[index], question: value }
			return newItems
		})
	}

	const handleAnswerChange = useCallback(
		(index: number) => (value: string | null) => {
			setItems(prev => {
				const newItems = [...prev]
				newItems[index] = { ...newItems[index], answer: value ?? "" }
				return newItems
			})
		},
		[],
	)

	const handleRemoveItem = (index: number) => {
		setItems(prev => prev.filter((_, i) => i !== index))
		setItemIds(prev => prev.filter((_, i) => i !== index))
	}

	const handleAddItem = () => {
		setItems(prev => [...prev, { question: "", answer: "" }])
		setItemIds(prev => [...prev, nextItemId.current++])
	}

	return (
		<EditorShell onClose={onClose}>
			<section className="px-6 pt-16 pb-8 sm:pt-20 lg:px-8">
				<div className="mx-auto max-w-3xl">
					<EditableField
						value={title}
						onChange={setTitle}
						placeholder="Section title"
						className="mb-10 text-center"
						maxCharacters={landingPageCopyCharacterLimits.title}
						inputClassName="text-center font-bold text-3xl text-gray-900 tracking-tight sm:text-4xl"
					/>
					<div className="divide-y divide-gray-200 rounded-2xl border border-gray-200 bg-white">
						{items.map((item, index) => (
							<div key={itemIds[index]} className="p-5 sm:p-6">
								<div className="mb-4 flex items-start justify-between gap-4">
									<EditableField
										value={item.question}
										onChange={value => handleQuestionChange(index, value)}
										placeholder="Question"
										maxCharacters={landingPageCopyCharacterLimits.title}
										inputClassName="font-semibold text-gray-900 text-lg tracking-tight"
									/>
									<button
										type="button"
										onClick={() => handleRemoveItem(index)}
										className="cursor-pointer pt-1 text-xs text-gray-400 transition-colors hover:text-red-600"
									>
										Remove
									</button>
								</div>
								<RichEditableField
									initialValue={item.answer}
									onChange={handleAnswerChange(index)}
									maxCharacters={landingPageCopyCharacterLimits.itemDescription}
								/>
							</div>
						))}
					</div>
					<Button variant="secondary" onClick={handleAddItem} className="mt-6 cursor-pointer">
						Add question
					</Button>
				</div>
			</section>
		</EditorShell>
	)
}

export default FaqBlockEditor
